import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    adminApproveReviewerProfileChangeRequestApi,
    adminFetchReviewerProfileChangeRequestsApi,
    adminRejectReviewerProfileChangeRequestApi,
} from '../../http/userAPI';

const PAGE_SIZE = 50;

const amazonSites = [
    { url: 'https://www.amazon.com', label: 'Amazon USA' },
    { url: 'https://www.amazon.co.uk', label: 'Amazon UK' },
    { url: 'https://www.amazon.ca', label: 'Amazon Canada' },
    { url: 'https://www.amazon.de', label: 'Amazon Germany' },
    { url: 'https://www.amazon.fr', label: 'Amazon France' },
    { url: 'https://www.amazon.it', label: 'Amazon Italy' },
    { url: 'https://www.amazon.es', label: 'Amazon Spain' },
    { url: 'https://www.amazon.co.jp', label: 'Amazon Japan' },
    { url: 'https://www.amazon.com.au', label: 'Amazon Australia' },
    { url: 'https://www.amazon.in', label: 'Amazon India' },
    { url: 'https://www.amazon.com.mx', label: 'Amazon Mexico' },
    { url: 'https://www.amazon.com.br', label: 'Amazon Brazil' },
];

const audibleSites = [
    { url: 'https://www.audible.com', label: 'Audible USA' },
    { url: 'https://www.audible.co.uk', label: 'Audible UK' },
];

const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'ALL', label: 'All' },
];

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const marketplaceLabel = (sites, value) => (
    sites.find((site) => site.url === value)?.label || value || '-'
);

const formatProfile = (profile) => {
    const safeProfile = profile || {};

    return [
        safeProfile.label ? `Label: ${safeProfile.label}` : null,
        safeProfile.amazon_name ? `Amazon: ${safeProfile.amazon_name}` : null,
        safeProfile.amazon_country ? marketplaceLabel(amazonSites, safeProfile.amazon_country) : null,
        safeProfile.audible_name ? `Audible: ${safeProfile.audible_name}` : null,
        safeProfile.audible_country ? marketplaceLabel(audibleSites, safeProfile.audible_country) : null,
        safeProfile.is_default ? 'Default' : null,
        safeProfile.status ? `Status: ${safeProfile.status}` : null,
    ].filter(Boolean).join(' | ') || '-';
};

const statusBadge = (status) => {
    if (status === 'APPROVED') return 'success';
    if (status === 'REJECTED') return 'danger';
    if (status === 'PENDING') return 'warning';
    return 'secondary';
};

const getApiErrorMessage = (error, fallback) => {
    const responseMessage = error?.response?.data?.message;
    if (responseMessage) return responseMessage;

    const status = error?.response?.status;
    if (status) return `${fallback} (${status})`;

    return error?.message || fallback;
};

const AdminReviewerProfileRequests = () => {
    const [status, setStatus] = useState('PENDING');
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [comments, setComments] = useState({});

    const pages = useMemo(() => Math.max(1, Math.ceil((total || 0) / PAGE_SIZE)), [total]);

    const load = async () => {
        try {
            setLoading(true);
            const data = await adminFetchReviewerProfileChangeRequestsApi({
                status,
                page,
                limit: PAGE_SIZE,
            });
            setRows(data?.requests || []);
            setTotal(Number(data?.total || 0));
        } catch (error) {
            console.error('Failed to load reviewer profile requests:', error);
            toast.error(getApiErrorMessage(error, 'Failed to load reviewer profile requests.'), {
                position: 'bottom-center',
                autoClose: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, page]);

    useEffect(() => {
        setPage(1);
    }, [status]);

    const setComment = (id, value) => {
        setComments((prev) => ({ ...prev, [id]: value }));
    };

    const approve = async (request) => {
        try {
            setSavingId(request.id);
            await adminApproveReviewerProfileChangeRequestApi(request.id, comments[request.id] || '');
            toast.success('Reviewer profile request approved.', {
                position: 'bottom-center',
                autoClose: 3000,
            });
            await load();
        } catch (error) {
            console.error('Approve reviewer profile request failed:', error);
            toast.error(getApiErrorMessage(error, 'Approve failed.'), {
                position: 'bottom-center',
                autoClose: 3000,
            });
        } finally {
            setSavingId(null);
        }
    };

    const reject = async (request) => {
        try {
            setSavingId(request.id);
            await adminRejectReviewerProfileChangeRequestApi(request.id, comments[request.id] || '');
            toast.success('Reviewer profile request rejected.', {
                position: 'bottom-center',
                autoClose: 3000,
            });
            await load();
        } catch (error) {
            console.error('Reject reviewer profile request failed:', error);
            toast.error(getApiErrorMessage(error, 'Reject failed.'), {
                position: 'bottom-center',
                autoClose: 3000,
            });
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Card className="p-3 w-100 mt-0">
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
                <div>
                    <h5 className="text-muted mb-1">Pending Reviewer Profile Requests</h5>
                    <div className="small text-muted">Approve applies payload, handles default set, syncs legacy user fields, and writes UserLogs.</div>
                </div>
                <div className="d-flex gap-2 align-items-end">
                    <Form.Group>
                        <Form.Label className="small text-muted mb-1">Status</Form.Label>
                        <Form.Select size="sm" value={status} onChange={(event) => setStatus(event.target.value)}>
                            {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Button variant="outline-primary" size="sm" onClick={load} disabled={loading}>
                        {loading ? <Spinner animation="border" size="sm" /> : 'Reload'}
                    </Button>
                </div>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover size="sm" className="small align-middle" style={{ minWidth: '1250px' }}>
                    <thead className="text-muted">
                    <tr>
                        <th>ID</th>
                        <th>User</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Current set</th>
                        <th>Payload</th>
                        <th>Staff comment</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan="9" className="text-center text-muted py-3">
                                {loading ? 'Loading...' : 'No requests found.'}
                            </td>
                        </tr>
                    ) : rows.map((request) => {
                        const isPending = request.status === 'PENDING';
                        const busy = savingId === request.id;
                        return (
                            <tr key={request.id}>
                                <td>#{request.id}</td>
                                <td>
                                    <div><strong>#{request.user?.id}</strong> {request.user?.username || '-'}</div>
                                    <div className="text-muted">{request.user?.email || '-'}</div>
                                    <div className="text-muted">{request.user?.tariff_plan || 'FREE'}</div>
                                </td>
                                <td>{request.request_type}</td>
                                <td><Badge bg={statusBadge(request.status)}>{request.status}</Badge></td>
                                <td>{formatDate(request.created_at)}</td>
                                <td>{formatProfile(request.targetProfile)}</td>
                                <td>{formatProfile(request.requested_payload)}</td>
                                <td style={{ minWidth: '220px' }}>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        size="sm"
                                        value={comments[request.id] ?? request.staff_comment ?? ''}
                                        onChange={(event) => setComment(request.id, event.target.value)}
                                        disabled={!isPending}
                                    />
                                </td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="success"
                                            size="sm"
                                            disabled={!isPending || busy}
                                            onClick={() => approve(request)}
                                        >
                                            {busy ? 'Saving...' : 'Approve'}
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            disabled={!isPending || busy}
                                            onClick={() => reject(request)}
                                        >
                                            Reject
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2">
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                    Previous
                </Button>
                <span className="small text-muted">Page {page} / {pages} · {total} request(s)</span>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page >= pages || loading}
                    onClick={() => setPage((prev) => Math.min(pages, prev + 1))}
                >
                    Next
                </Button>
            </div>
        </Card>
    );
};

export default AdminReviewerProfileRequests;
